// src/components/issues/ChoixQualification.tsx — SCE Issues
// Choix de la famille et de la criticité d'un problème.
//
// Partagés par le bandeau de qualification et le formulaire d'édition
// (Lot 6) : un coordinateur qui qualifie puis corrige retrouve exactement le
// même geste — mêmes bulles, même seuil de bascule en liste, mêmes couleurs.
// Écrits deux fois, ils auraient dérivé au premier ajustement.

import type { JSX } from 'react'
import { Bulle, GroupeBulles, type CouleurBulle } from '@/components/ui/Bulle'
import {
  A_QUALIFIER,
  CRITICITES_TRANCHEES,
  type Criticite,
  type CriticiteTranchee,
} from '@/types/issue.types'

/**
 * Au-delà de ce nombre de familles, le choix passe en liste déroulante :
 * validé pour les onze familles ELEC et AUTOM, mais les treize familles MECA
 * (24/09) repoussaient le bouton de validation hors de l'écran.
 */
const SEUIL_BULLES_FAMILLES = 11

/**
 * Couleurs Traffic Light d'une bulle de criticité sélectionnée. Majeur garde
 * un contour noir : un fond jaune sur carte blanche n'a sinon plus de bord.
 */
const COULEURS_CRITICITE: Record<CriticiteTranchee, CouleurBulle> = {
  Critique: { fond: 'var(--color-traffic-red)', texte: 'var(--color-white)', bord: 'var(--color-traffic-red)' },
  Majeur: { fond: 'var(--color-traffic-yellow)', texte: 'var(--color-black)', bord: 'var(--color-black)' },
  Mineur: { fond: 'var(--color-traffic-green)', texte: 'var(--color-white)', bord: 'var(--color-traffic-green)' },
}

const STYLE_ETIQUETTE = {
  fontSize: 'var(--font-size-info)',
  color: 'var(--color-text-secondary)',
  display: 'block',
  marginBottom: 6,
} as const

export function ChoixFamille({
  id,
  familles,
  valeur,
  onChange,
  desactive,
}: {
  /** Identifiant du <select>, quand la liste bascule en déroulant. */
  id: string
  familles: readonly string[]
  valeur: string
  onChange: (v: string) => void
  desactive?: boolean
}): JSX.Element {
  if (familles.length > SEUIL_BULLES_FAMILLES) {
    // Une famille historique, retirée de la liste depuis (« Erreur conception
    // mécanique »), reste affichée telle quelle plutôt que de passer pour
    // « Choisir une famille… ».
    const horsListe = valeur !== '' && !familles.includes(valeur)
    return (
      <>
        <label htmlFor={id} style={STYLE_ETIQUETTE}>
          Famille
        </label>
        <select
          id={id}
          value={valeur}
          onChange={(e) => onChange(e.target.value)}
          disabled={desactive}
          style={{
            width: '100%',
            height: 48,
            padding: '0 12px',
            borderRadius: 8,
            border: '1px solid var(--color-border-input)',
            backgroundColor: 'var(--color-white)',
            color: 'var(--color-text-primary)',
            fontSize: 'var(--font-size-copy2)',
            fontFamily: 'inherit',
          }}
        >
          <option value="" disabled={valeur !== ''}>Choisir une famille…</option>
          {horsListe && <option value={valeur}>{valeur} (ancienne famille)</option>}
          {familles.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>
      </>
    )
  }
  return (
    <>
      <span style={STYLE_ETIQUETTE}>Famille</span>
      <GroupeBulles libelle="Famille">
        {familles.map((f) => (
          <Bulle
            key={f}
            role="radio"
            libelle={f}
            selectionne={valeur === f}
            onClick={() => onChange(f)}
            desactive={desactive}
          />
        ))}
      </GroupeBulles>
    </>
  )
}

export function ChoixCriticite<V extends Criticite = CriticiteTranchee>({
  valeur,
  onChange,
  desactive,
  avecAQualifier,
}: {
  valeur: V | null
  onChange: (v: V) => void
  desactive?: boolean
  /**
   * Propose aussi « À qualifier » — administrateur seulement (Lot 7) : il
   * peut remettre un problème en attente de qualification.
   */
  avecAQualifier?: boolean
}): JSX.Element {
  return (
    <>
      <span style={STYLE_ETIQUETTE}>Criticité</span>
      {/* La pastille montre la couleur Traffic Light AVANT la sélection :
          le coordinateur lit « rouge = critique » sans avoir à essayer. */}
      <GroupeBulles libelle="Criticité">
        {CRITICITES_TRANCHEES.map((c) => (
          <Bulle
            key={c}
            role="radio"
            libelle={c}
            selectionne={valeur === c}
            onClick={() => onChange(c as V)}
            desactive={desactive}
            pastille={COULEURS_CRITICITE[c].fond}
            couleurSelection={COULEURS_CRITICITE[c]}
          />
        ))}
        {avecAQualifier && (
          <Bulle
            role="radio"
            libelle={A_QUALIFIER}
            selectionne={valeur === A_QUALIFIER}
            onClick={() => onChange(A_QUALIFIER as V)}
            desactive={desactive}
          />
        )}
      </GroupeBulles>
    </>
  )
}
